import type { FarePriceBreakdown } from "@b2bportal/air-shopping-api";
import {
  type BundledPackageState,
  type BundledPackageStateToFareId,
  PackageType,
} from "@b2bportal/core-packages";
import type { Prices } from "@b2bportal/core-types";
import type { IFlightFares } from "@overrides/types";

/**
 * Resolves the price to display for a flight fare inside the package funnel.
 *
 * - Fixed/grid packages (`tenantSpecific` present) show the absolute fixed
 *   points cost plus the remaining package cash — returned as a
 *   [[FarePriceBreakdown]] (`baseAmount` = fixed points, `taxesAndFees` = the
 *   package total cash) so the price display renders "<points> + <cash>"
 *   (via `getFixedPriceDisplay`) instead of the flex "<points> or <cash>".
 *   Mirrors the hotel headline card. This is an absolute price, not a delta, so
 *   callers must not apply incremental/signed display for it (see
 *   `isFixedPackageFare`).
 * - FLIGHT_CAR packages start on the flights page, so there is no prior
 *   selection to be incremental from: show the package total.
 * - Every other package type shows the incremental change from the
 *   currently selected package, falling back to the total when missing.
 */
export const getPackageFarePrice = (
  packageState: BundledPackageState | undefined,
  packageType: PackageType | undefined
): Prices | FarePriceBreakdown | undefined => {
  if (!packageState?.price) return undefined;

  const { price } = packageState;

  if (price.tenantSpecific) {
    return {
      baseAmount: price.tenantSpecific,
      taxesAndFees: price.totalPrice,
      // package pricing is already a total; the provider's itemised taxes are not part of it
      taxLineItems: [],
    };
  }

  return packageType === PackageType.FLIGHT_CAR
    ? price.totalPrice
    : (price.incrementalChange ?? price.totalPrice);
};

/**
 * Whether a fare's package price is a fixed/grid (absolute) price rather than
 * an incremental delta. Callers use this to suppress signed/incremental display
 * for fixed-points fares.
 */
export const isFixedPackageFare = (
  packageState: BundledPackageState | undefined
): boolean => Boolean(packageState?.price?.tenantSpecific);

/**
 * Replaces the raw flight-shop price on each fare-class entry with the
 * matching package price. Fares without a package entry are left untouched.
 *
 * The flight shop response inside the package funnel carries flight-only
 * amounts (currently zero), while the real overage lives in
 * packageStateByFareId. Anything that derives a display price from
 * IFlightFares (e.g. the collapsed mobile flight card summary) needs this
 * substitution applied first.
 */
export const applyPackagePricingToFareData = (
  fares: IFlightFares,
  packageStateByFareId: BundledPackageStateToFareId | undefined,
  packageType: PackageType | undefined
): IFlightFares => {
  if (!packageStateByFareId) return fares;

  return Object.entries(fares).reduce((acc, [fareClass, fare]) => {
    const packagePrice = getPackageFarePrice(
      packageStateByFareId[fare.fareId],
      packageType
    );

    acc[fareClass] = packagePrice ? { ...fare, price: packagePrice } : fare;
    return acc;
  }, {} as IFlightFares);
};
